import { getSubSteps } from '#models/general/steps';

import { getHouseKeepingFields } from './houseKeeping';
import { getMashingAndCollectionFields } from './mashingAndCollection';
import { getMashDetailsFields } from './mashDetails';
import { getMillingFields } from './milling';
import { getWortAnalysisFields } from './wortAnalysis';
import styles from './styles.module.scss';

export const MASH_SUBSTEP_TARGET = 'AdelphiMashStepModal';

export const MASH_SUBSTEPS_IDS = {
  MILLING: 'millingAdelphi',
  MASH_DETAILS: 'mashDetailsAdelphi',
  MASHING_AND_COLLECTION: 'mashingAndCollectionAdelphi',
  WORT_ANALYSIS: 'wortAnalysisAdelphi',
  HOUSE_KEEPING: 'houseKeepingAdelphi'
};

const MASH_NEXT_SUBSTEPS = {
  [MASH_SUBSTEPS_IDS.MILLING]: MASH_SUBSTEPS_IDS.MASH_DETAILS,
  [MASH_SUBSTEPS_IDS.MASH_DETAILS]: MASH_SUBSTEPS_IDS.MASHING_AND_COLLECTION,
  [MASH_SUBSTEPS_IDS.MASHING_AND_COLLECTION]: MASH_SUBSTEPS_IDS.WORT_ANALYSIS,
  [MASH_SUBSTEPS_IDS.WORT_ANALYSIS]: MASH_SUBSTEPS_IDS.HOUSE_KEEPING
};

const MASH_PREVIOUS_SUBSTEPS = {
  [MASH_SUBSTEPS_IDS.MASH_DETAILS]: MASH_SUBSTEPS_IDS.MILLING,
  [MASH_SUBSTEPS_IDS.MASHING_AND_COLLECTION]: MASH_SUBSTEPS_IDS.MASH_DETAILS,
  [MASH_SUBSTEPS_IDS.WORT_ANALYSIS]: MASH_SUBSTEPS_IDS.MASHING_AND_COLLECTION,
  [MASH_SUBSTEPS_IDS.HOUSE_KEEPING]: MASH_SUBSTEPS_IDS.WORT_ANALYSIS
};

export const MASH_SUBSTEPS = getSubSteps(
  MASH_SUBSTEP_TARGET,
  MASH_SUBSTEPS_IDS,
  MASH_NEXT_SUBSTEPS,
  MASH_PREVIOUS_SUBSTEPS
);

export const MASH_COMPLETE_FIELDS = {
  [MASH_SUBSTEPS_IDS.MILLING]: getMillingFields,
  [MASH_SUBSTEPS_IDS.MASH_DETAILS]: getMashDetailsFields,
  [MASH_SUBSTEPS_IDS.MASHING_AND_COLLECTION]: getMashingAndCollectionFields,
  [MASH_SUBSTEPS_IDS.WORT_ANALYSIS]: getWortAnalysisFields,
  [MASH_SUBSTEPS_IDS.HOUSE_KEEPING]: getHouseKeepingFields
};

export const MASH_SUBSTEPS_ADDITION_PROPS = {
  [MASH_SUBSTEPS_IDS.MILLING]: {
    oneColumnForm: true
  },
  [MASH_SUBSTEPS_IDS.MASH_DETAILS]: {
    oneColumnForm: true
  },
  [MASH_SUBSTEPS_IDS.MASHING_AND_COLLECTION]: {
    buttonsContainerClassName: styles.mashingAndCollectionButtons
  },
  [MASH_SUBSTEPS_IDS.WORT_ANALYSIS]: {
    oneColumnForm: true
  },
  [MASH_SUBSTEPS_IDS.HOUSE_KEEPING]: {
    oneColumnForm: true
  }
};

export const ADELPHI_MASH_SUBSTEPS_INFO = {
  target: MASH_SUBSTEP_TARGET,
  substeps: MASH_SUBSTEPS,
  fields: MASH_COMPLETE_FIELDS,
  additionalProps: MASH_SUBSTEPS_ADDITION_PROPS
};

export const MILLING_FIELDS = {
  START_DATE_AND_TIME: 'startDateAndTime',
  FIRST_MALT_BIN_NO: 'firstMaltBinNo',
  SECOND_MALT_BIN_NO: 'secondMaltBinNo',
  FIRST_MALT_BIN_PEATED: 'firstMaltBinPeated',
  SECOND_MALT_BIN_PEATED: 'secondMaltBinPeated',
  FIRST_MALT_BIN_PEATED_MANUAL: 'firstMaltBinPeatedManual',
  SECOND_MALT_BIN_PEATED_MANUAL: 'secondMaltBinPeatedManual',
  GRIST_ANALYSIS: 'gristAnalysis',
  HUSK_RATIO: 'huskRatio',
  GRIST_RATIO: 'gristRatio',
  FLOUR_RATIO: 'flourRatio',
  FAILURE: 'millingFailure'
};

export const MASH_DETAILS_FIELDS = {
  MASH_ID: 'mashingId',
  FILL_ID: 'fillId',
  MASH_DATE: 'mashDate',
  WB_NO: 'wbNo',
  MALT_WEIGHT: 'maltWeight',
  FIRST_MALT_WEIGHT_PERCENTAGE: 'firstMaltWeightPercentage',
  SECOND_MALT_WEIGHT_PERCENTAGE: 'secondMaltWeightPercentage',
  MALT_VARIETY: 'maltVariety',
  MALT_VARIETY_MANUAL: 'maltVarietyManual',
  PEATED: 'peatedTitle',
  PEATED_MANUAL: 'peatedTitleManual'
};

export const MASHING_AND_COLLECTION_FIELDS = {
  FIRST_WATER_SPROUT_TEMP: 'firstWaterSproutTemp',
  FIRST_WATER_LITRES: 'firstWaterLitres',
  FIRST_TIME_MASH_IN: 'firstTimeMashIn',
  SETTLING_TIME: 'settlingTime',
  RECIRCULATION_TIME: 'recirculationTime',
  FIRST_START_DRAIN_TIME: 'firstStartDrainTime',
  SECOND_WATER_SPROUT_TEMP: 'secondWaterSproutTemp',
  SECOND_WATER_LITRES: 'secondWaterLitres',
  SECOND_TIME_MASH_IN: 'secondTimeMashIn',
  SECOND_TIME_DRAINED: 'secondDrainedTime',
  THIRD_WATER_SPROUT_TEMP: 'thirdWaterSproutTemp',
  THIRD_WATER_LITRES: 'thirdWaterLitres',
  THIRD_TIME_MASH_IN: 'thirdTimeMashIn',
  THIRD_START_DRAIN_TIME: 'thirdStartDrainTime',
  THIRD_TIME_DRAINED: 'thirdDrainedTime',
  YEAST_MANAGEMENT: 'yeastManagement',
  YEAST_PITCHED_TIME: 'yeastPitchedTime',
  DRAFF_OUT_START_TIME: 'draffOutStartTime',
  FAILURE: 'mashingAndCollectionFailure',
  FIRST_YEAST_VARIETY: 'firstYeastVariety',
  FIRST_YEAST_VARIETY_MANUAL: 'firstYeastVarietyManual',
  SECOND_YEAST_VARIETY: 'secondYeastVariety',
  SECOND_YEAST_VARIETY_MANUAL: 'secondYeastVarietyManual',
  FIRST_YEAST_WEIGHT: 'firstYeastWeight',
  SECOND_YEAST_WEIGHT: 'secondYeastWeight'
};

export const WORT_ANALYSIS_FIELDS = {
  WORT_DIP: 'wortDip',
  WORT_LITRES: 'wortLitres',
  WORT_TEMP: 'wortTemp',
  ORIGINAL_GRAVITY: 'originalGravity',
  MTE: 'mte',
  PSY: 'psy',
  WASHBACK_SEALED: 'washbackSealed'
};

export const HOUSE_KEEPING_FIELDS = {
  WORTS_LINE_FLOWRATE: 'wortsLineFlowrate',
  PROCESS_WATER_METER_READING: 'processWaterMeterReading',
  CAUSTIC_CLEANED: 'causticCleaned',
  MASH_TUN_OPERATOR: 'mashTunOperator',
  MASH_TUN_NOTES: 'mashTunNotes'
};

export const PEATED_OPTIONS = {
  UNPEATED: 'unpeated',
  PEATED: 'peated',
  HEAVILY_PEATED: 'heavilyPeated',
  MANUAL: 'manual'
};
